import "../fit-dropdown/fit-simple-dropdown.js";
import { LitElement, html } from "lit";
import Pikaday from "pikaday";
import pikadayDefaults from "../../js/elements/pikaday-defaults";
import "../fit-spinner/fit-spinner.js";
import configureStore from "./configureStore";
import {
  requestWizardDatesIfNeeded,
  SELECT_DURATION,
  SELECT_MEALPLAN,
  SELECT_ROOMTYPE,
  requestWizardRateIfNeeded,
  SELECT_ARRIVAL,
} from "./actions";
import styles from "./fit-price-wizard.css";

class FitPriceWizard extends LitElement {
  static get is() {
    return "fit-price-wizard";
  }

  static get styles() {
    return styles;
  }

  render() {
    const dateAvailable =
      this.state.dates &&
      this.state.selectedArrival &&
      this.state.dates.hasOwnProperty(this.state.selectedArrival);

    return html`
      <label for="mealplan" class="fit-price-wizard__label"
        >${this.state.labels.Mealplan.Label}</label
      >
      <fit-simple-dropdown
        id="mealplan"
        class="fit-price-wizard__input"
        .options="${this.state.mealplans}"
        selected="${this.state.selectedMealplan}"
        @fit-dropdown-selected="${this._handleMealplanSelection}"
      ></fit-simple-dropdown>

      <label for="roomtype" class="fit-price-wizard__label"
        >${this.state.labels.Roomtype.Label}</label
      >
      <fit-simple-dropdown
        id="roomtype"
        class="fit-price-wizard__input"
        .options="${this.state.roomtypes}"
        selected="${this.state.selectedRoomtype}"
        @fit-dropdown-selected="${this._handleRoomtypeSelection}"
      ></fit-simple-dropdown>

      <label for="duration" class="fit-price-wizard__label"
        >${this.state.labels.Duration.Label}</label
      >
      <fit-simple-dropdown
        id="duration"
        class="fit-price-wizard__input"
        .options="${this.state.durationOptions}"
        selected="${this.state.selectedDuration}"
        @fit-dropdown-selected="${this._handleDurationSelection}"
      ></fit-simple-dropdown>

      <label for="arrival-trigger" class="fit-price-wizard__label"
        >${this.state.labels.Arrival.Label}</label
      >
      <input
        type="text"
        id="arrival-trigger"
        class="acts-as-select"
        placeholder="${this.state.arrival.placeholder}"
        .value="${this.state.arrival.text || ""}"
        ?filled="${this.state.arrival.text}"
        ?hidden="${!this.state.wizardIsFetchingDates && !this.state.dates}"
        readonly
      />
      <input
        name="df"
        type="hidden"
        id="arrival-input"
        class="fit-price-wizard__input fit-price-wizard__input--arrival"
        .value="${this.state.selectedArrival}"
        hidden
        @change="${this._handleArrivalSelection}"
      />
      ${!this.state.wizardIsFetchingDates && !dateAvailable
        ? html`
            <div class="shard shard--warning shard--shallow">
              <span class="shard__fragment fa fa-exclamation-circle"></span>
              <p class="shard__fragment">${this.state.labels.Arrival.Empty}</p>
            </div>
          `
        : ""}
      <div id="arrival-container"></div>

      <div ?hidden="${!this.state.selectedArrival}">
        <div ?hidden="${!this.state.rate.PriceString}">
          <p class="fit-price-wizard__price">
            ${this.state.rate.PriceString}
            <small>${this.state.labels.DisplayPrice.Suffix}</small>
          </p>
          <p
            class="fit-price-wizard__price fit-price-wizard__price--small"
            ?hidden="${this.state.roomSingle || false}"
          >
            ${this.state.rate.TotalPriceString}
            <small>${this.state.labels.DisplayTotalPrice.Suffix}</small>
          </p>
        </div>

        <form id="cta-form" method="get" action="${this.state.button.url}">
          <button
            class="button button--expanded fit-price-wizard__submit"
            ?disabled="${this.state.wizardIsFetchingRate || false}"
          >
            ${this.state.button.text}
          </button>
          <fit-spinner
            spinner-color="#fff"
            ?hidden="${!this.state.wizardIsFetchingRate}"
          ></fit-spinner>
        </form>

        ${this.state.rate.Message
          ? html`<p class="fit-price-wizard__priceinfo">
              ${this.state.rate.Message}
            </p>`
          : ""}
        ${this.state.rate.ReservationPossible
          ? html`
              <a
                href="${this.state.labels.ReservationUrl}"
                target="${this.state.labels.Relaunch == "True"
                  ? "modal"
                  : "fit-modal"}"
                class="cta-sidebar__reservation acts-as-input"
              >
                <span
                  class="cta-sidebar__reservation-icon fa fa-2x fa-fit-72h"
                ></span>
                <span class="cta-sidebar__reservation-text"
                  >${this.state.labels.Button.Reserve}</span
                >
                <span class="cta-sidebar__reservation-info"></span>
              </a>
            `
          : ""}
        <form
          ?hidden="${!this.state.rate.AdditionalRequest}"
          id="cta-additional-request-form"
          method="get"
          action="${this.state.button.additionalRequestUrl}"
        >
          <button
            class="button button--expanded button--inverted fit-price-wizard__submit"
          >
            ${this.state.labels.Button.AdditionalRequest}
          </button>
        </form>
      </div>
    `;
  }

  static get properties() {
    return {
      state: {
        type: Object,
      },
    };
  }

  constructor(props) {
    super(props);

    this.store = configureStore(this.attributes);
    this.store.subscribe(() => this.stateChanged(this.store.getState()));

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.initializeIfVisible();
          this.intersectionObserver.disconnect();
        }
      },
      { root: document.body }
    );

    this.state = null;
    this._today = new Date();
    this._maxDate = new Date();
    this._maxDate.setFullYear(this._today.getFullYear() + 2);
  }

  connectedCallback() {
    super.connectedCallback();
    this.intersectionObserver.observe(this);
    this.initializeIfVisible();
  }

  shouldUpdate() {
    return this.state;
  }

  firstUpdated(changedProperties) {
    this.$ = {};
    this.$.arrivalContainer =
      this.shadowRoot.querySelector("#arrival-container");
    this.$.arrivalInput = this.shadowRoot.querySelector("#arrival-input");
    this.$.arrivalTrigger = this.shadowRoot.querySelector("#arrival-trigger");
    this._initializeDatePickers();
  }

  stateChanged(state) {
    this.state = state;

    if (this.datepicker) {
      const config = this.datepicker._o;
      if (state.minDate !== config.minDate) {
        this.datepicker.setMinDate(state.minDate);
      }
      if (state.maxDate !== config.maxDate) {
        this.datepicker.setMaxDate(state.maxDate);
      }
    }
  }

  initializeIfVisible() {
    if (this.initialized || !this._isVisible()) {
      return;
    }
    this.store.dispatch(requestWizardDatesIfNeeded("/json/dates/"));
    this.store.dispatch(
      requestWizardRateIfNeeded("/json/availability/", {
        type: SELECT_ARRIVAL,
        selectedArrival: this.state.selectedArrival,
      })
    );
    this.initialized = true;
  }

  _initializeDatePickers() {
    pikadayDefaults.then((loadedPikadayDefaults) => {
      this.datepicker = this._buildDatepicker(
        this.$.arrivalInput,
        this.$.arrivalContainer,
        this.$.arrivalTrigger,
        {},
        Pikaday,
        loadedPikadayDefaults
      );
    });
  }

  _buildDatepicker(
    input,
    container,
    trigger,
    config,
    Pikaday,
    pikadayDefaults
  ) {
    return new Pikaday(
      Object.assign(Object.assign(pikadayDefaults, config), {
        field: input,
        container,
        trigger,
        format: "YYYYMMDD",
        position: "bottom right",
        firstDay: 1,
        theme: "fit-price-wizard",
        onClose() {
          this.el.classList.remove("is-visible");
        }, // has to be function here, because ES6 arrow function binds local "this"
        onBeforeDraw: (documentFragment) => {
          this._injectDatesIntoDatepicker(documentFragment);
        },
      })
    );
  }

  _injectDatesIntoDatepicker(documentFragment) {
    if (!this.state.dates) {
      return;
    }

    Array.prototype.forEach.call(
      documentFragment.querySelectorAll("button.pika-day"),
      (button) => {
        // attention, pika months start counting at 0 for january
        const date = `${button.dataset.pikaYear}${`0${
          1 + parseInt(button.dataset.pikaMonth)
        }`.slice(-2)}${`0${button.dataset.pikaDay}`.slice(-2)}`;
        if (this.state.dates[date]) {
          const dateData = this.state.dates[date];
          dateData.Allotment
            ? button.parentNode.classList.add("has-allotment")
            : button.parentNode.classList.remove("has-allotment");
          const priceButton = button.cloneNode();
          priceButton.classList.add("pika-price-button");
          priceButton.innerHTML = dateData.PriceString;
          button.parentNode.appendChild(priceButton);
        } else {
          button.parentNode.classList.add("is-disabled");
        }
      }
    );

    if (
      this.state.labels &&
      this.state.labels.Arrival &&
      this.state.labels.Arrival.HasAllotment &&
      this.state.labels.Arrival.OnRequest
    ) {
      const legend = document.createElement("ul");
      legend.classList.add("pika-legend");
      const hasAllotment = document.createElement("li");
      hasAllotment.classList.add("pika-legend__has-allotment");
      hasAllotment.textContent = this.state.labels.Arrival.HasAllotment;
      const onRequest = document.createElement("li");
      onRequest.classList.add("pika-legend__on-request");
      onRequest.textContent = this.state.labels.Arrival.OnRequest;
      legend.appendChild(hasAllotment);
      legend.appendChild(onRequest);
      documentFragment.appendChild(legend);
    }

    Array.prototype.forEach.call(
      documentFragment.querySelectorAll("*"),
      (el) => {
        el.classList.add("fit-price-wizard");
      }
    );
  }

  _handleMealplanSelection(event) {
    if (event.target.selected !== this.state.selectedMealplan) {
      this.store.dispatch(
        requestWizardDatesIfNeeded("/json/dates/", "/json/availability/", {
          type: SELECT_MEALPLAN,
          selectedMealplan: event.target.selected,
        })
      );
    }
  }

  _handleRoomtypeSelection(event) {
    if (event.target.selected !== this.state.selectedRoomtype) {
      this.store.dispatch(
        requestWizardDatesIfNeeded("/json/dates/", "/json/availability/", {
          type: SELECT_ROOMTYPE,
          selectedRoomtype: event.target.selected,
        })
      );
    }
  }

  _handleDurationSelection(event) {
    if (event.target.selected !== this.state.selectedDuration) {
      this.store.dispatch(
        requestWizardDatesIfNeeded("/json/dates/", "/json/availability/", {
          type: SELECT_DURATION,
          selectedDuration: event.target.selected,
        })
      );
    }
  }

  _handleArrivalSelection(event) {
    if (event.target.value !== this.state.selectedArrival) {
      this.store.dispatch(
        requestWizardRateIfNeeded("/json/availability/", {
          type: SELECT_ARRIVAL,
          selectedArrival: event.target.value,
        })
      );
    }
  }

  _isVisible() {
    return this.offsetParent !== null;
  }
}

customElements.define(FitPriceWizard.is, FitPriceWizard);
